import { Action } from 'redux';

/**
 * Redux actions for managing site admin resource permissions.
 * 
 * These actions handle fetching and storing the allowed site admin resources
 * from GetActorVisibleResourceTypesRequest API, which returns which admin
 * resource types (User, Group, Task, Job) the current user can access.
 * 
 * Protected by isAdminProfilesEnabledOnServer - only dispatched when admin profiles is enabled.
 */

export const FETCH_SITE_ADMIN_RESOURCES_REQUEST = 'FETCH_SITE_ADMIN_RESOURCES_REQUEST';
export const FETCH_SITE_ADMIN_RESOURCES_SUCCESS = 'FETCH_SITE_ADMIN_RESOURCES_SUCCESS';
export const FETCH_SITE_ADMIN_RESOURCES_FAILURE = 'FETCH_SITE_ADMIN_RESOURCES_FAILURE';
export const CLEAR_SITE_ADMIN_RESOURCES = 'CLEAR_SITE_ADMIN_RESOURCES';

export interface FetchSiteAdminResourcesRequestAction extends Action {
  type: typeof FETCH_SITE_ADMIN_RESOURCES_REQUEST;
}

export interface FetchSiteAdminResourcesSuccessAction extends Action {
  type: typeof FETCH_SITE_ADMIN_RESOURCES_SUCCESS;
  allowedResources: Set<string>;
}

export interface FetchSiteAdminResourcesFailureAction extends Action {
  type: typeof FETCH_SITE_ADMIN_RESOURCES_FAILURE;
}

export interface ClearSiteAdminResourcesAction extends Action {
  type: typeof CLEAR_SITE_ADMIN_RESOURCES;
}

export type SiteAdminResourceActionTypes =
  | FetchSiteAdminResourcesRequestAction
  | FetchSiteAdminResourcesSuccessAction
  | FetchSiteAdminResourcesFailureAction
  | ClearSiteAdminResourcesAction;

/**
 * Action creator to request fetching site admin resources.
 * Triggers saga to call GetActorVisibleResourceTypesRequest API.
 */
export function fetchSiteAdminResources(): FetchSiteAdminResourcesRequestAction {
  return {
    type: FETCH_SITE_ADMIN_RESOURCES_REQUEST
  };
}

/**
 * Action creator for successful fetch.
 * @param allowedResources - Set of resource types the user can access (e.g., 'User', 'Group')
 */
export function fetchSiteAdminResourcesSuccess(allowedResources: Set<string>): FetchSiteAdminResourcesSuccessAction {
  return {
    type: FETCH_SITE_ADMIN_RESOURCES_SUCCESS,
    allowedResources
  };
}

/**
 * Action creator for failed fetch.
 */
export function fetchSiteAdminResourcesFailure(): FetchSiteAdminResourcesFailureAction {
  return {
    type: FETCH_SITE_ADMIN_RESOURCES_FAILURE
  };
}

/**
 * Action creator to clear site admin resources.
 * Used when switching sites or when user status changes.
 */
export function clearSiteAdminResources(): ClearSiteAdminResourcesAction {
  return {
    type: CLEAR_SITE_ADMIN_RESOURCES
  };
}
